import { useI18n } from '@ecom/i18n';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useUnlockAccountMutation } from '../api';
import { KIND_LABEL_KEY, type LockedAccount } from '../types';

import { PasswordActionDialog } from './PasswordActionDialog';

import { useToast } from '@/components/ui/toast/ToastProvider';

/** Accounts locked right now by repeated failed sign-ins. Unlocking asks a reason AND the superadmin's own password again. */
export function LockedAccountsTable({
  accounts,
}: {
  readonly accounts: readonly LockedAccount[];
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const mutation = useUnlockAccountMutation();
  const [target, setTarget] = useState<LockedAccount | null>(null);

  return (
    <>
      <Table caption={t('admin.access.locked.caption')}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('admin.access.col.account')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.access.col.kind')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.access.col.failures')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.access.col.lockedUntil')}</TableHeaderCell>
            <TableHeaderCell> </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => (
            <TableRow key={account.userId}>
              <TableCell>
                <span className="text-sm text-ink">{account.displayName ?? account.email}</span>
                <p className="text-xs text-inkFaint">{account.email}</p>
              </TableCell>
              <TableCell>
                <Badge tone={account.kind === 'superadmin' ? 'accent' : 'neutral'}>
                  {t(KIND_LABEL_KEY[account.kind])}
                </Badge>
              </TableCell>
              <TableCell>
                <span className="text-sm text-ink">{account.failedLoginCount}</span>
              </TableCell>
              <TableCell>
                <span className="text-xs text-inkFaint">
                  {new Date(account.lockedUntil).toLocaleString('fr-TN')}
                </span>
              </TableCell>
              <TableCell align="end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setTarget(account);
                  }}
                >
                  {t('admin.access.action.unlock')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <PasswordActionDialog
        open={target !== null}
        title={t('admin.access.unlockTitle')}
        warning={t('admin.access.unlockWarning')}
        confirmLabel={t('merchant.common.confirm')}
        {...(target === null ? {} : { subject: target.displayName ?? target.email })}
        onClose={() => {
          setTarget(null);
        }}
        onConfirm={async ({ reason, password }) => {
          if (target === null) return;
          await mutation.mutateAsync({ userId: target.userId, reason, confirmPassword: password });
          toast.success(t('admin.access.unlockedToast'));
          setTarget(null);
        }}
      />
    </>
  );
}
